import { useState, useEffect, useCallback } from 'react'
import {
  Activity, RefreshCw, TrendingUp, TrendingDown, Minus,
  Brain, Cpu, Building2, Hash, Search,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, AreaChart, Area,
} from 'recharts'
import { api } from '../lib/api'

// ── Hype Cycle Phases ────────────────────────────────────────────────────────
// Map mention velocity to a Gartner-style hype cycle phase

function classifyPhase(mentions: number, avgMentions: number): { phase: string; color: string; description: string } {
  const ratio = mentions / Math.max(avgMentions, 1)
  if (ratio > 3) return { phase: 'Peak of Inflated Expectations', color: '#f43f5e', description: 'Massive hype — every source is covering this' }
  if (ratio > 2) return { phase: 'Innovation Trigger', color: '#f59e0b', description: 'Rapid growth in mentions — early excitement' }
  if (ratio > 1.2) return { phase: 'Slope of Enlightenment', color: '#10b981', description: 'Steady, growing coverage — real adoption signals' }
  if (ratio > 0.5) return { phase: 'Plateau of Productivity', color: '#3b82f6', description: 'Stable mentions — mature technology' }
  return { phase: 'Trough of Disillusionment', color: '#64748b', description: 'Declining mentions — hype fading' }
}

const COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e', '#ec4899', '#84cc16', '#64748b', '#a78bfa']

function generateSparkData(mentions: number, avg: number): { v: number }[] {
  // Generate a plausible 8-point sparkline based on mention count
  const ratio = mentions / Math.max(avg, 1)
  const points = []
  for (let i = 0; i < 8; i++) {
    let base: number
    if (ratio > 2) base = mentions * (0.3 + i * 0.1) // rising sharply
    else if (ratio > 1.2) base = mentions * (0.5 + i * 0.07) // rising steadily
    else if (ratio > 0.5) base = mentions * (0.8 + Math.sin(i * 0.7) * 0.1) // stable
    else base = mentions * (1.2 - i * 0.08) // declining
    points.push({ v: Math.max(1, Math.round(base + (Math.random() - 0.5) * mentions * 0.15)) })
  }
  return points
}

export default function HypeCyclePage() {
  const [trends, setTrends] = useState<any>(null)
  const [tab, setTab] = useState<'technologies' | 'companies' | 'topics'>('technologies')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/trends/live')
      setTrends(data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 300_000); return () => clearInterval(i) }, [fetchData])

  const dataMap = {
    technologies: trends?.trending_technologies || [],
    companies: trends?.trending_companies || [],
    topics: trends?.trending_topics || [],
  }

  const items = dataMap[tab]
  const avgMentions = items.length > 0 ? items.reduce((s: number, i: any) => s + i.mentions, 0) / items.length : 1

  const filtered = items.filter((item: any) =>
    !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Build hype cycle chart data
  const hypeCycleData = filtered.map((item: any) => {
    const phase = classifyPhase(item.mentions, avgMentions)
    return { ...item, ...phase }
  })

  // Sort by mentions for the bar chart
  const barData = filtered.slice(0, 15).map((item: any) => ({
    name: item.name.length > 20 ? item.name.slice(0, 20) + '...' : item.name,
    mentions: item.mentions,
  }))

  const TAB_CONFIG = {
    technologies: { icon: Cpu, label: 'Technologies' },
    companies: { icon: Building2, label: 'Companies' },
    topics: { icon: Hash, label: 'Topics' },
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={24} className="text-purple" /> Hype Cycle & Tech Radar
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Technology lifecycle tracking from {trends?.total_articles_scanned || 0} articles across 120+ feeds
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !trends && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Scanning articles for technology trends...</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {(Object.entries(TAB_CONFIG) as [keyof typeof TAB_CONFIG, typeof TAB_CONFIG[keyof typeof TAB_CONFIG]][]).map(([key, cfg]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={clsx('badge text-xs cursor-pointer px-4 py-2 transition-colors',
              tab === key ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            <cfg.icon size={14} /> {cfg.label} ({dataMap[key].length})
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="input pl-9 py-2 text-xs" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mention Bar Chart */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-text-primary mb-4">Mention Volume</h2>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#18181c', border: '1px solid #26262c', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="mentions" radius={[0, 4, 4, 0]}>
                  {barData.map((_: any, idx: number) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-text-muted text-center py-16">No data</p>}
        </div>

        {/* Hype Cycle Classification */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-text-primary mb-4">Hype Cycle Phase</h2>
          {/* Header row */}
          <div className="flex items-center gap-2 px-1 pb-2 border-b border-border text-[9px] text-text-muted uppercase tracking-wider">
            <span className="w-5">#</span>
            <span className="w-36">Technology</span>
            <span className="flex-1">Trend</span>
            <span className="w-14 text-right">Mentions</span>
          </div>
          <div className="space-y-0 max-h-[400px] overflow-y-auto">
            {hypeCycleData.map((item: any, idx: number) => (
              <div key={item.name} className="flex items-center gap-2 py-2 border-b border-border/50 last:border-0 hover:bg-surface-hover/30 transition-colors px-1">
                <span className="text-[10px] text-text-muted font-mono w-5 shrink-0">#{idx + 1}</span>
                <div className="w-36 shrink-0 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-text-primary truncate">{item.name}</p>
                    <p className="text-[9px] truncate" style={{ color: item.color }}>{item.phase}</p>
                  </div>
                </div>
                {/* Sparkline fills remaining space */}
                <div className="flex-1 h-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={generateSparkData(item.mentions, avgMentions)} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                      <defs>
                        <linearGradient id={`spark-${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={item.color} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={item.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke={item.color} strokeWidth={1.5} fill={`url(#spark-${idx})`} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-14 text-right shrink-0">
                  <span className="text-sm font-mono font-bold text-text-primary">{item.mentions}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phase Legend */}
      <div className="card p-4">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Hype Cycle Phases</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { phase: 'Innovation Trigger', color: '#f59e0b', desc: 'Rapid growth in mentions' },
            { phase: 'Peak of Inflated Expectations', color: '#f43f5e', desc: 'Maximum hype' },
            { phase: 'Trough of Disillusionment', color: '#64748b', desc: 'Declining interest' },
            { phase: 'Slope of Enlightenment', color: '#10b981', desc: 'Real adoption signals' },
            { phase: 'Plateau of Productivity', color: '#3b82f6', desc: 'Mature, stable' },
          ].map(p => (
            <div key={p.phase} className="flex items-start gap-2">
              <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5" style={{ backgroundColor: p.color }} />
              <div>
                <p className="text-[11px] font-medium text-text-primary">{p.phase}</p>
                <p className="text-[10px] text-text-muted">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top movers with articles */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-4">Top Movers — Recent Articles</h2>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {filtered.slice(0, 8).map((item: any) => (
            <div key={item.name} className="border-b border-border pb-3 last:border-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-semibold text-text-primary">{item.name}</span>
                <span className="badge-blue text-[10px]">{item.mentions} mentions</span>
              </div>
              {(item.articles || []).slice(0, 3).map((a: any, i: number) => (
                <div key={i} className="flex items-start gap-2 py-1">
                  <span className="text-[10px] text-text-muted shrink-0">{a.source}</span>
                  <p className="text-xs text-text-secondary">
                    {a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline hover:text-text-primary">{a.title}</a> : a.title}
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
